import { ColumnDef } from "@tanstack/react-table";
import { CrossCircleIcon, EditIcon, NavIcons, PhoneIcon } from "assets/icons";
import { t } from "i18next";
import moment from "moment";
import { Checkbox, TableItemActionsControl } from "ui";

import { AVAILABLE_TO_CANCEL_ORDER_STATUSES } from "../../constants";
import { Order } from "../../orders.types";
import { CallConfirmationBadge } from "../CallConfirmationBadge";
import {
  OrderCell,
  OrderPersonCell,
  OrderProblemNotesTooltip,
  OrderStatusCell,
} from "./atoms";
import { GetColumnsParams } from "./OrdersTable.types";

import styles from "./OrdersTable.module.scss";

export const getOrderPageColumns = ({
  ordersList,
  selectedOrdersIds,
  setSelectedOrdersIds,
  statusesList,
  getOrderStatusIndex,
  openedItemOptionsIndex,
  setOpenedItemOptionsIndex,
  onCancelOrder,
  onAssignDriver,
  onOpenCustomerDetails,
  onOpenConversation,
}: GetColumnsParams): Array<ColumnDef<Order, Order[]>> => {
  return [
    {
      id: "select",
      header: () => {
        const isChecked = ordersList.some((order) =>
          selectedOrdersIds?.includes(order.id),
        );
        const isFullyChecked = ordersList.every((order) =>
          selectedOrdersIds?.includes(order.id),
        );

        return (
          <Checkbox
            className={styles.ordersTable_checkboxCell}
            checked={isChecked}
            partiallyChecked={!isFullyChecked}
            onChange={() => {
              if (isChecked) {
                setSelectedOrdersIds?.([]);
              } else {
                setSelectedOrdersIds?.((prev) => [
                  ...prev,
                  ...ordersList.map((order) => order.id),
                ]);
              }
            }}
          />
        );
      },
      size: 4,
      minSize: 4,
      cell: ({ row }) => {
        const isSelected = selectedOrdersIds?.includes(row.original.id);

        return (
          <div
            className={styles.ordersTable_checkboxCell}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={isSelected}
              onChange={() => {
                setSelectedOrdersIds?.((prev) =>
                  isSelected
                    ? prev.filter((id) => id !== row.original.id)
                    : [...prev, row.original.id],
                );
              }}
            />
          </div>
        );
      },
    },
    {
      header: t("Order"),
      size: 13,
      minSize: 13,
      cell: ({ row }) => <OrderCell order={row.original} />,
    },
    {
      id: "resellerName",
      accessorKey: "resellerName",
      header: t("User"),
      size: 14,
      minSize: 14,
      cell: ({ row }) => (
        <div className={styles.ordersTable_resellerCell}>
          <OrderProblemNotesTooltip
            tooltipId={`problem-notes-tooltip-${row.original.id}`}
            problemNotesForAdmin={row.original.problemNotesForAdmin}
          />
          <OrderPersonCell
            imageUrl={row.original.resellerImageUrl}
            name={row.original.resellerName}
          />
        </div>
      ),
    },
    {
      id: "orderTotal",
      accessorKey: "orderTotal",
      header: t("Order total"),
      size: 10,
      minSize: 10,
      cell: ({ row }) => row.original.orderTotal.toLocaleString("en-US"),
    },
    {
      id: "totalCommission",
      accessorKey: "totalCommission",
      header: t("Commission"),
      size: 10,
      minSize: 10,
      cell: ({ row }) =>
        Math.floor(row.original.totalCommission).toLocaleString("en-US"),
    },
    {
      id: "totalMargin",
      accessorKey: "totalMargin",
      header: t("Margin"),
      size: 8,
      minSize: 8,
      cell: ({ row }) =>
        Math.floor(row.original.totalMargin).toLocaleString("en-US"),
    },
    {
      id: "itemsCount",
      accessorKey: "itemsCount",
      header: t("Items"),
      size: 5,
      minSize: 5,
      cell: ({ row }) => row.original.itemsCount,
    },
    {
      header: t("Driver"),
      size: 14,
      minSize: 14,
      cell: ({ row }) => {
        const {
          driverName,
          driverImageUrl,
          deliveryCompanyName,
          deliveryCompanyAvatarUrl,
        } = row.original;

        if (deliveryCompanyName && driverName) {
          return (
            <div className={styles.ordersTable_companyDriverStack}>
              <OrderPersonCell
                imageUrl={deliveryCompanyAvatarUrl ?? undefined}
                name={deliveryCompanyName}
              />
              <span className={styles.ordersTable_assignedToLabel}>
                {t("Assigned to")}
              </span>
              <OrderPersonCell imageUrl={driverImageUrl} name={driverName} />
            </div>
          );
        }
        if (driverName) {
          return (
            <OrderPersonCell imageUrl={driverImageUrl} name={driverName} />
          );
        }
        if (deliveryCompanyName) {
          return (
            <OrderPersonCell
              imageUrl={deliveryCompanyAvatarUrl ?? undefined}
              name={deliveryCompanyName}
            />
          );
        }
        return (
          <p className={styles.ordersTable_noDriver}>{t("Not assigned")}</p>
        );
      },
    },
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: t("Order date"),
      size: 10,
      minSize: 10,
      cell: ({ row }) => (
        <pre className={styles.ordersTable_dateCell}>
          {`${moment(row.original.createdAt).format("DD/MM/YYYY")}`}
          {"\n"}
          {`${moment(row.original.createdAt).format("HH:mm")}`}
        </pre>
      ),
    },
    {
      header: t("Status"),
      size: 12,
      minSize: 12,
      cell: ({ row }) => (
        <OrderStatusCell
          order={row.original}
          statusesList={statusesList}
          activeStatusIndex={getOrderStatusIndex(row.original.status)}
        />
      ),
    },
    {
      id: "callCustomer",
      header: () => null,
      size: 4,
      minSize: 4,
      cell: ({ row }) => (
        <button
          type="button"
          className={styles.ordersTable_callButton}
          title={t("Customer details")}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenCustomerDetails?.(row.original);
          }}
        >
          <PhoneIcon width={18} height={18} />
        </button>
      ),
      enableSorting: false,
    },
    {
      id: "callConfirmation",
      header: t("Confirmation"),
      size: 11,
      minSize: 11,
      cell: ({ row }) => (
        <CallConfirmationBadge
          result={row.original.lastCallConfirmationResult}
          at={row.original.lastCallConfirmationAt}
        />
      ),
      enableSorting: false,
    },

    {
      accessorKey: "dotsColumn",
      header: () => null,
      size: 3,
      minSize: 3,
      cell: ({ row }) => {
        const canCancel = AVAILABLE_TO_CANCEL_ORDER_STATUSES.includes(
          row.original.status,
        );
        const actionsList = [
          ...(onAssignDriver
            ? [
                {
                  label: t("Assign Driver"),
                  Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
                  onClick: () => onAssignDriver(row.original),
                },
              ]
            : []),
          ...(canCancel
            ? [
                {
                  label: t("Cancel"),
                  Icon: (
                    <CrossCircleIcon fill={"#090914"} width={18} height={18} />
                  ),
                  onClick: () => onCancelOrder?.(row.original),
                },
              ]
            : []),
          ...(onOpenConversation && row.original.conversationId
            ? [
                {
                  label: t("Open the conversation"),
                  Icon: <NavIcons.ConversationsIcon width={18} height={18} />,
                  onClick: () => onOpenConversation(row.original),
                },
              ]
            : []),
        ];

        return actionsList.length ? (
          <TableItemActionsControl
            actionsList={actionsList}
            rowIndex={row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        ) : null;
      },
      enableSorting: false,
    },
  ];
};

export default getOrderPageColumns;
